import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Search, CheckCircle, XCircle, Clock, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

const VulnerabilityDisclosurePolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "20 décembre 2024";
  const companyName = "EasyFinder";
  const securityEmail = "security@easyfinder.fr";
  const websiteUrl = "https://easyfinder.fr";

  return (
    <>
      <SEO
        title="Politique de Divulgation des Vulnérabilités | EasyFinder"
        description="Politique de divulgation responsable des vulnérabilités d'EasyFinder. Comment signaler une faille de sécurité de manière responsable."
        canonical="/vulnerability-disclosure-policy"
      />
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <LanguageSwitcher frPath="/vulnerability-disclosure-policy" enPath="/vulnerability-disclosure-policy-en" />
          </div>

          <article className="prose prose-slate dark:prose-invert max-w-none">
            <div className="flex items-center gap-3 mb-4">
              <Search className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold mb-0">Politique de Divulgation des Vulnérabilités</h1>
            </div>
            <p className="text-muted-foreground mb-8">
              Dernière mise à jour : {lastUpdated}
            </p>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p>
                {companyName} s'engage à protéger la sécurité des données de ses utilisateurs. 
                Nous apprécions l'aide de la communauté de la sécurité informatique pour identifier 
                et corriger les vulnérabilités de nos systèmes.
              </p>
              <p>
                Cette politique fournit aux chercheurs en sécurité des directives claires pour mener 
                des activités de découverte de vulnérabilités et explique comment nous signaler 
                les failles découvertes de manière responsable.
              </p>
            </section>

            {/* Guidelines */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="h-6 w-6" />
                2. Directives pour les Chercheurs
              </h2>
              <p>Nous vous demandons de :</p>
              <ul>
                <li>
                  <strong>Nous notifier rapidement</strong> après avoir découvert une vulnérabilité 
                  réelle ou potentielle
                </li>
                <li>
                  <strong>Nous accorder un délai raisonnable</strong> (minimum 90 jours) pour résoudre 
                  le problème avant toute divulgation publique
                </li>
                <li>
                  <strong>Éviter les violations de vie privée</strong>, la dégradation de l'expérience 
                  utilisateur, la perturbation des systèmes de production et la destruction ou manipulation de données
                </li>
                <li>
                  <strong>Limiter les exploits</strong> au strict nécessaire pour confirmer l'existence 
                  d'une vulnérabilité
                </li>
                <li>
                  <strong>Ne pas utiliser d'exploit</strong> pour compromettre ou obtenir des données, 
                  établir un accès persistant ou pivoter vers d'autres systèmes
                </li>
                <li>
                  <strong>Arrêter immédiatement vos tests</strong> si vous rencontrez des données sensibles 
                  (données personnelles, informations financières, secrets commerciaux) et nous notifier
                </li>
                <li>
                  <strong>Ne pas soumettre</strong> un grand volume de rapports de faible qualité
                </li>
              </ul>
            </section>

            {/* Safe Harbor */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Clause de Non-Poursuite (Safe Harbor)</h2>
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-green-800 dark:text-green-200">
                  La recherche en sécurité menée conformément à cette politique est considérée comme :
                </p>
                <ul className="text-green-700 dark:text-green-300 mt-2">
                  <li>✅ <strong>Autorisée</strong> au regard de toute loi anti-piratage applicable</li>
                  <li>✅ <strong>Autorisée</strong> au regard de toute loi sur la protection des données</li>
                  <li>✅ <strong>Exempte</strong> de restrictions dans nos Conditions d'Utilisation qui 
                      pourraient interférer avec la recherche en sécurité</li>
                </ul>
                <p className="text-green-800 dark:text-green-200 mt-4 font-semibold">
                  Nous travaillerons avec vous pour comprendre et résoudre rapidement le problème, 
                  et {companyName} ne suggèrera ni ne poursuivra d'action en justice en relation 
                  avec votre recherche menée conformément à cette politique.
                </p>
              </div>
            </section>

            {/* Scope */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Périmètre</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Systèmes dans le périmètre
              </h3>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Système</th>
                    <th className="border border-border p-3 text-left">URL / Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Application Web principale</td>
                    <td className="border border-border p-3">
                      <a href={websiteUrl} className="text-primary underline">{websiteUrl}</a>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">API Backend</td>
                    <td className="border border-border p-3">
                      Endpoints Supabase Edge Functions accessibles via {websiteUrl}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Authentification</td>
                    <td className="border border-border p-3">
                      Flux OAuth Google et authentification email/mot de passe
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Partage de documents</td>
                    <td className="border border-border p-3">
                      Liens de partage : {websiteUrl}/share/* et {websiteUrl}/collection/*
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-medium mt-6 mb-3 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Hors périmètre
              </h3>
              <p>Les systèmes et services suivants sont <strong>explicitement exclus</strong> du périmètre :</p>
              <ul>
                <li>Infrastructure de nos sous-traitants (Supabase, Cloudflare, Stripe, Google)</li>
                <li>Services tiers intégrés (Gmail API, Dropbox API)</li>
                <li>Applications tierces non développées par {companyName}</li>
                <li>Tout domaine non listé ci-dessus</li>
              </ul>
              <p className="mt-4">
                Les vulnérabilités découvertes dans des solutions tierces doivent être signalées 
                directement au fournisseur concerné selon sa propre politique de divulgation.
              </p>
              <p>
                <strong>En cas de doute</strong> sur l'appartenance d'un système au périmètre, 
                contactez-nous à <a href={`mailto:${securityEmail}`} className="text-primary underline">{securityEmail}</a> avant de commencer vos tests.
              </p>
            </section>

            {/* Prohibited Testing */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Types de Tests Interdits</h2>
              <p>Les types de tests suivants ne sont <strong>pas autorisés</strong> :</p>
              
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                <ul className="text-red-700 dark:text-red-300 space-y-2">
                  <li>❌ <strong>Tests de déni de service (DoS/DDoS)</strong> réseau ou applicatif</li>
                  <li>❌ <strong>Tests physiques</strong> (accès aux locaux, tailgating)</li>
                  <li>❌ <strong>Ingénierie sociale</strong> (phishing, vishing, pretexting) contre nos employés ou utilisateurs</li>
                  <li>❌ <strong>Spam ou envoi massif</strong> de messages</li>
                  <li>❌ <strong>Tests automatisés agressifs</strong> sans rate limiting approprié</li>
                  <li>❌ <strong>Modification ou suppression</strong> de données d'autres utilisateurs</li>
                  <li>❌ <strong>Accès aux comptes</strong> d'autres utilisateurs sans leur consentement explicite</li>
                </ul>
              </div>
            </section>

            {/* Qualifying Vulnerabilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Vulnérabilités Éligibles</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">Exemples de vulnérabilités que nous recherchons</h3>
              <ul>
                <li>Injection SQL, NoSQL ou de commandes</li>
                <li>Cross-Site Scripting (XSS) - Stored, Reflected, DOM-based</li>
                <li>Cross-Site Request Forgery (CSRF)</li>
                <li>Contournement d'authentification ou d'autorisation</li>
                <li>Insecure Direct Object Reference (IDOR)</li>
                <li>Exposition de données sensibles</li>
                <li>Server-Side Request Forgery (SSRF)</li>
                <li>Mauvaise configuration de sécurité critique</li>
                <li>Failles dans la logique métier permettant des actions non autorisées</li>
                <li>Contournement des politiques RLS (Row Level Security)</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">Vulnérabilités généralement hors scope</h3>
              <ul>
                <li>Vulnérabilités nécessitant un accès physique à l'appareil de l'utilisateur</li>
                <li>Rapports de scanners automatisés sans preuve d'exploitabilité</li>
                <li>Clickjacking sur des pages sans actions sensibles</li>
                <li>Self-XSS (XSS nécessitant l'interaction de la victime)</li>
                <li>Absence de headers de sécurité sans démonstration d'impact</li>
                <li>Énumération d'utilisateurs via les messages d'erreur de login</li>
                <li>Politique de mot de passe faible (déjà documentée)</li>
                <li>Absence de rate limiting sans impact démontré</li>
                <li>Divulgation de version de logiciel sans vulnérabilité associée</li>
              </ul>
            </section>

            {/* Reporting */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Mail className="h-6 w-6" />
                7. Comment Signaler une Vulnérabilité
              </h2>
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
                <p className="text-lg font-semibold mb-2">📧 Email de signalement :</p>
                <a href={`mailto:${securityEmail}`} className="text-2xl text-primary underline font-mono">
                  {securityEmail}
                </a>
                <p className="text-sm text-muted-foreground mt-2">
                  Les rapports peuvent être soumis de manière anonyme.
                </p>
              </div>

              <h3 className="text-xl font-medium mt-6 mb-3">Informations à inclure dans votre rapport</h3>
              <p>Pour nous permettre de traiter efficacement votre signalement, merci d'inclure :</p>
              
              <div className="bg-muted rounded-lg p-4 font-mono text-sm mt-4">
                <p><strong>📋 RAPPORT DE VULNÉRABILITÉ</strong></p>
                <br />
                <p><strong>1. Description de la vulnérabilité :</strong></p>
                <p>[Décrivez clairement le problème de sécurité]</p>
                <br />
                <p><strong>2. Localisation :</strong></p>
                <p>[URL, endpoint, fonctionnalité concernée]</p>
                <br />
                <p><strong>3. Impact potentiel :</strong></p>
                <p>[Quelles données ou fonctions sont à risque ?]</p>
                <br />
                <p><strong>4. Étapes de reproduction :</strong></p>
                <p>1. [Première étape]</p>
                <p>2. [Deuxième étape]</p>
                <p>3. [...]</p>
                <br />
                <p><strong>5. Preuves :</strong></p>
                <p>[Captures d'écran, requêtes HTTP, logs - SANS données d'autres utilisateurs]</p>
                <br />
                <p><strong>6. Suggestion de correction (optionnel) :</strong></p>
                <p>[Vos recommandations pour corriger la faille]</p>
                <br />
                <p><strong>7. Vos coordonnées (optionnel) :</strong></p>
                <p>[Si vous souhaitez être tenu informé de la résolution]</p>
              </div>

              <p className="mt-4">
                <strong>Langue :</strong> Les rapports en français ou en anglais sont acceptés.
              </p>
            </section>

            {/* Our Commitment */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-6 w-6" />
                8. Nos Engagements
              </h2>
              
              <p>Lorsque vous choisissez de partager vos coordonnées, nous nous engageons à :</p>
              
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Engagement</th>
                    <th className="border border-border p-3 text-left">Délai</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Accusé de réception de votre rapport</td>
                    <td className="border border-border p-3 font-semibold">≤ 3 jours ouvrés</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Confirmation de la validité de la vulnérabilité</td>
                    <td className="border border-border p-3 font-semibold">≤ 10 jours ouvrés</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Mise à jour sur l'avancement de la correction</td>
                    <td className="border border-border p-3 font-semibold">Régulière</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Notification de la résolution</td>
                    <td className="border border-border p-3 font-semibold">Dès déploiement</td>
                  </tr>
                </tbody>
              </table>

              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  💬 Communication transparente
                </h4>
                <p className="text-blue-700 dark:text-blue-300">
                  Nous nous engageons à communiquer de manière transparente et respectueuse. 
                  Nous sommes ouverts à la discussion et prêts à répondre à vos questions 
                  concernant le traitement de votre signalement.
                </p>
              </div>
            </section>

            {/* Recognition */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Reconnaissance</h2>
              <p>
                Bien que nous n'ayons pas actuellement de programme de bug bounty rémunéré, 
                nous apprécions sincèrement les contributions de la communauté de sécurité.
              </p>
              <p>
                Avec votre accord, nous pouvons :
              </p>
              <ul>
                <li>Vous mentionner dans notre Hall of Fame (page de remerciements)</li>
                <li>Vous fournir une lettre de recommandation</li>
                <li>Vous tenir informé des résolutions et améliorations apportées</li>
              </ul>
            </section>

            {/* Legal */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Considérations Légales</h2>
              <p>
                Cette politique ne constitue pas une autorisation de contourner les lois applicables. 
                Les chercheurs sont responsables de s'assurer que leurs activités sont conformes 
                aux lois de leur juridiction.
              </p>
              <p>
                En participant à ce programme de divulgation responsable, vous acceptez de ne pas 
                divulguer publiquement les détails de la vulnérabilité avant :
              </p>
              <ul>
                <li>Que nous ayons confirmé la correction du problème, <strong>OU</strong></li>
                <li>90 jours après votre signalement initial (selon ce qui survient en premier)</li>
              </ul>
              <p>
                Nous vous contacterons si nous avons besoin de plus de temps pour résoudre 
                un problème complexe.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Contact</h2>
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <p className="mb-4">
                  Pour toute question concernant cette politique ou pour signaler une vulnérabilité :
                </p>
                <p className="text-lg">
                  <strong>Email :</strong>{" "}
                  <a href={`mailto:${securityEmail}`} className="text-primary underline">
                    {securityEmail}
                  </a>
                </p>
                <p className="text-sm text-muted-foreground mt-4">
                  Nous vous remercions pour votre contribution à la sécurité d'{companyName} et de ses utilisateurs.
                </p>
              </div>
            </section>

          </article>

          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {companyName}. Tous droits réservés.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default VulnerabilityDisclosurePolicy;
